import { useRef, useState } from "react";
import {
  Box,
  Button,
  Chip,
  Dialog,
  DialogActions,
  DialogContent,
  DialogTitle,
  FormControl,
  IconButton,
  InputLabel,
  MenuItem,
  Select,
  Typography,
} from "@mui/material";
import CloseIcon from "@mui/icons-material/Close";
import UploadFileIcon from "@mui/icons-material/UploadFile";
import InsertDriveFileIcon from "@mui/icons-material/InsertDriveFile";
import CheckCircleIcon from "@mui/icons-material/CheckCircle";
import RestaurantIcon from "@mui/icons-material/Restaurant";
import { formatFechaCorta } from "../../../common/utils/formatDate";
import { importarMenuExcel } from "../service/importarMenuService";

const DIAS_SEMANA = ["lunes", "martes", "miercoles", "jueves", "viernes"];

const ImportarMenuModal = ({ open, onClose, proveedores = [], proveedorInicial, onImportado, showToast }) => {
  const [idProveedor, setIdProveedor] = useState(proveedorInicial || "");
  const [file, setFile] = useState(null);
  const [loading, setLoading] = useState(false);
  const [resultado, setResultado] = useState(null);
  const fileInputRef = useRef(null);

  const handleClose = () => {
    setFile(null);
    setResultado(null);
    setLoading(false);
    onClose();
  };

  const handleFileChange = (e) => {
    const selected = e.target.files?.[0];
    if (selected) setFile(selected);
  };

  const handleImportar = async () => {
    if (!idProveedor) {
      showToast("Elegí el proveedor al que pertenecen los platos.", "warning");
      return;
    }
    if (!file) {
      showToast("Adjuntá el archivo Excel (.xlsx).", "warning");
      return;
    }

    setLoading(true);
    try {
      const res = await importarMenuExcel(file, idProveedor);
      setResultado(res);
      showToast(res.message || "Menú importado correctamente.", "success");
    } catch (err) {
      showToast(err.message || "Error al importar el archivo.", "error");
    } finally {
      setLoading(false);
    }
  };

  const handleIrASemana = () => {
    onImportado(resultado, idProveedor);
    handleClose();
  };

  return (
    <Dialog open={open} onClose={handleClose} fullWidth maxWidth="sm">
      <DialogTitle sx={{ display: "flex", justifyContent: "space-between", alignItems: "center" }}>
        <Box sx={{ display: "flex", alignItems: "center", gap: 1 }}>
          <UploadFileIcon color="primary" />
          <Typography variant="h6" sx={{ fontWeight: 700 }}>
            Importar menú semanal desde Excel
          </Typography>
        </Box>
        <IconButton onClick={handleClose} size="small">
          <CloseIcon />
        </IconButton>
      </DialogTitle>

      <DialogContent dividers>
        {!resultado ? (
          <Box sx={{ display: "flex", flexDirection: "column", gap: 2.5, pt: 1 }}>
            <Typography variant="body2" color="text.secondary">
              El archivo debe tener dos columnas (día y plato). El sistema calcula
              automáticamente la próxima semana a partir de la fecha de hoy y carga la
              disponibilidad de esos platos.
            </Typography>

            <FormControl fullWidth>
              <InputLabel>Proveedor</InputLabel>
              <Select
                label="Proveedor"
                value={idProveedor}
                onChange={(e) => setIdProveedor(e.target.value)}
              >
                {proveedores.map((p) => (
                  <MenuItem key={p.id} value={p.id}>
                    {p.nombre}
                  </MenuItem>
                ))}
              </Select>
            </FormControl>

            <Box
              onClick={() => fileInputRef.current?.click()}
              sx={{
                border: "2px dashed rgba(31,97,141,0.3)",
                borderRadius: 3,
                p: 3,
                textAlign: "center",
                cursor: "pointer",
                bgcolor: "rgba(31,97,141,0.03)",
                "&:hover": { bgcolor: "rgba(31,97,141,0.06)" },
              }}
            >
              <input
                ref={fileInputRef}
                type="file"
                accept=".xlsx"
                hidden
                onChange={handleFileChange}
              />
              {file ? (
                <Box sx={{ display: "flex", alignItems: "center", justifyContent: "center", gap: 1 }}>
                  <InsertDriveFileIcon color="primary" />
                  <Typography sx={{ fontWeight: 600 }}>{file.name}</Typography>
                </Box>
              ) : (
                <>
                  <UploadFileIcon sx={{ fontSize: 32, color: "#1F618D", opacity: 0.6 }} />
                  <Typography sx={{ fontWeight: 600, mt: 1 }}>
                    Hacé clic para elegir el archivo .xlsx
                  </Typography>
                </>
              )}
            </Box>
          </Box>
        ) : (
          <Box sx={{ display: "flex", flexDirection: "column", gap: 2, pt: 1 }}>
            <Box sx={{ display: "flex", alignItems: "center", gap: 1, color: "success.main" }}>
              <CheckCircleIcon />
              <Typography sx={{ fontWeight: 700 }}>{resultado.message}</Typography>
            </Box>

            <Box>
              <Typography variant="body2" sx={{ fontWeight: 600, mb: 1 }}>
                Semana cargada
              </Typography>
              <Box sx={{ display: "flex", flexWrap: "wrap", gap: 1 }}>
                {DIAS_SEMANA.map((dia) =>
                  resultado.semana?.[dia] ? (
                    <Chip
                      key={dia}
                      label={`${dia.charAt(0).toUpperCase() + dia.slice(1)} ${formatFechaCorta(
                        resultado.semana[dia],
                      )}`}
                      sx={{ textTransform: "capitalize", fontWeight: 600 }}
                    />
                  ) : null,
                )}
              </Box>
            </Box>

            <Box>
              <Typography variant="body2" sx={{ fontWeight: 600, mb: 1 }}>
                Platos importados
              </Typography>
              <Box sx={{ display: "flex", flexDirection: "column", gap: 0.5 }}>
                {(resultado.platos_importados || []).map((plato) => (
                  <Box key={plato} sx={{ display: "flex", alignItems: "center", gap: 1 }}>
                    <RestaurantIcon sx={{ fontSize: 16, color: "#1F618D" }} />
                    <Typography variant="body2">{plato}</Typography>
                  </Box>
                ))}
              </Box>
            </Box>
          </Box>
        )}
      </DialogContent>

      <DialogActions sx={{ p: 2 }}>
        {!resultado ? (
          <>
            <Button onClick={handleClose} color="inherit">
              Cancelar
            </Button>
            <Button variant="contained" onClick={handleImportar} disabled={loading}>
              {loading ? "Importando..." : "Importar"}
            </Button>
          </>
        ) : (
          <>
            <Button onClick={handleClose} color="inherit">
              Cerrar
            </Button>
            <Button variant="contained" onClick={handleIrASemana}>
              Ir a esa semana
            </Button>
          </>
        )}
      </DialogActions>
    </Dialog>
  );
};

export default ImportarMenuModal;
